import { apiClient } from '@/services/api-client'
import { useAuthStore } from '@/stores/auth'

// P13-V7B (refutation): the canonical name. It was the legacy alias 'operador_ruta'.
const ROUTE_OPERATOR_ROLE = 'route_operator'
const ROUTE_OPERATOR_DISABLED_SURFACE = {
  success: false,
  status: 'NOT_AVAILABLE',
  code: 'ROUTE_OPERATOR_GENERIC_SURFACE_DISABLED',
  message: 'Superficie generica de operador de ruta deshabilitada: usar vistas especificas con datos reales self-scoped.'
}

export class BaseRoleService {
  constructor(role) {
    this.role = role
    this.baseEndpoints = this.getEndpointsByRole(role)
  }

  /**
   * Obtener headers de autenticación
   */
  getAuthHeaders() {
    const authStore = useAuthStore()
    const token = authStore.token
    
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }
  }

  /**
   * ── P13-V5A-R5 · THE ENDPOINT TABLE NOW DESCRIBES ENDPOINTS THAT EXIST ──
   *
   * This table declared 21 targets across 7 roles. Measured against backend main `38d3779b`
   * (post-R4) exactly TWO of them resolved. The rest fell into three groups:
   *
   *   NEVER MOUNTED    /api/analytics/system-wide, /api/analytics/admin-kpis,
   *                    /api/analytics/readonly-kpis, /api/dashboard/super-admin,
   *                    /api/dashboard/analista, /api/admin/actions,
   *                    /api/admin/limited-actions, /api/analista/enabled-actions,
   *                    /supervisor/zone-actions, /api/agency/employee-actions,
   *                    /api/department/head-actions
   *   RETIRED 501      /api/dashboard/{admin,agency-employee,department-head}
   *                    (INLINE_SYNTHETIC_DASHBOARD_REMOVED) and
   *                    /api/analytics/department-kpis (no mature source this phase)
   *   RETIRED BY       /api/agency/employee-kpis — withdrawn on a binding decision of George
   *   DECISION         (agencyStateRoutes.js:392)
   *
   * A second defect hid the last real one. `apiClient` sets `baseURL` to `/api`
   * (config/runtimeConfig.ts:37-51), so every entry written as `/api/...` composed
   * `/api/api/...` and could never resolve — including `/api/route-operator/kpis`, which is a
   * REAL producer (route-operator.js:506) whose response fields `route_progress`,
   * `visits_today` and `performance_today` are exactly the ones `useRoleKPIs` reads. A live
   * capability was dark because of a path bug, and the 404 it produced was then published as
   * business figures by the `|| 0` fallbacks downstream. Repairing the path RESTORES a real
   * capability; it is the counterweight to everything R5 retires.
   *
   * The table now lists ONLY verified endpoints. A role with no producer says so, and the
   * consumer publishes nothing for it — an absent endpoint is not an observation of zero.
   *
   * P13-V7B (refutation) — THE TABLE IS KEYED BY CANONICAL ROLE NAMES. It was keyed by the legacy
   * aliases the mounting views wrote by hand (`supervisor_zona`, `operador_ruta`, plus five
   * `superadmin`/`administrator`/`analista`/`empleado_agencia`/`jefe_departamento` entries that all
   * said NO_PRODUCER), so a hand-written spelling chose the endpoint. The shell now passes the
   * SESSION's canonical role; only `field_supervisor` has a producer, and every other name —
   * canonical or legacy — falls to NO_PRODUCER, i.e. no request at all.
   */
  getEndpointsByRole(role) {
    const NO_PRODUCER = { dashboard: null, kpis: null, actions: null }
    const endpoints = {
      field_supervisor: {
        // W2 · ROLE-HOME-API-FIELD-SUPERVISOR — `dashboard` is now NO_PRODUCER for this role.
        //
        // `GET /api/field-supervisor/dashboard` is one of the 38 uncontracted role-home siblings the
        // W2 perimeter contains to vendor-only, so a client request for it now answers 403. It was
        // being issued AUTOMATICALLY on every landing mount (`BaseRoleDashboard` calls
        // `loadRoleData()` for every role that is not route_operator/agency_employee) and its result
        // was never rendered: `roleData` appears nowhere in that component's template. An automatic
        // read of an unsold capability whose answer nothing displays is not worth preserving
        // admission for, and §13.1 says so in as many words.
        //
        // `kpis` is UNTOUCHED. The visible KPI strip reads `/analytics/zone-kpis`, a SIGNED Analytics
        // route whose payload PR #56/#213 already narrowed — a different surface with a different
        // owner, and the reason this landing stays useful after the containment.
        dashboard: null,
        kpis: '/analytics/zone-kpis',
        actions: null
      },
      route_operator: {
        // W2 · WS5 · K1 — ROUTE-OPERATOR-FIRST-DEMO-PERIMETER-CONVERGENCE — `kpis` is NO_PRODUCER.
        //
        // `GET /api/route-operator/kpis` EXISTS and is real (route-operator.js:506, reads over
        // RouteExecution / ExecutionAgencyVisit) — R5 repaired its path and that repair stands.
        // What changed is AUTHORITY: backend child K1 rebinds it to
        // `ROUTE_OPERATOR_DEFERRED_V4_ROLES = ['super_admin']`, because what it publishes is route
        // PROGRESS (route_progress, visits_today, performance_today) — full-V4 capability that
        // `PILOT_SCOPE_CONTRACT.md` §DEFERRED_NOT_CLOSED leaves out of the first demo — and not the
        // incidence KPIs Scenario B signs. For the delivered identity that producer can only answer
        // 403, so the landing must not ask.
        //
        // With `kpis: null`, `getKPIs` issues NO request and returns `NO_PRODUCER`, which
        // `useRoleKPIs.formatKPIsByRole` renders as ZERO cards and no degradation notice
        // (P13-V5A-R5: an absent producer is not announced as an unavailable one).
        dashboard: null,
        kpis: null,
        actions: null
      }
    }
    return Object.prototype.hasOwnProperty.call(endpoints, role) ? endpoints[role] : NO_PRODUCER
  }

  /**
   * P13-V5A-R5 — a request is issued only when a verified producer exists. Previously the
   * service always issued one, and the 404 that came back was converted into KPI cards.
   */
  static noProducer(role, kind) {
    return {
      success: false,
      status: 'NO_PRODUCER',
      code: 'ROLE_SURFACE_HAS_NO_CANONICAL_PRODUCER',
      role,
      kind,
      message: `No existe un productor canonico para ${kind} del rol ${role}.`,
      timestamp: new Date().toISOString()
    }
  }

  async getDashboardData(params = {}) {
    if (this.role === ROUTE_OPERATOR_ROLE) {
      return {
        ...ROUTE_OPERATOR_DISABLED_SURFACE,
        role: this.role,
        data: null,
        timestamp: new Date().toISOString()
      }
    }

    // P13-V5A-R5: no verified producer -> no request, and nothing publishable.
    if (!this.baseEndpoints.dashboard) {
      return BaseRoleService.noProducer(this.role, 'dashboard')
    }

    try {
      const response = await apiClient.get(this.baseEndpoints.dashboard, {
        params: { role: this.role, ...params }
      })
      return response.data
    } catch (error) {
      console.warn(`⚠️ Backend no disponible para ${this.role} dashboard:`, error.message)

      return this.getFallbackDashboardData()
    }
  }

  async getKPIs(params = {}, options = {}) {
    // P13-V5A-R5: see getEndpointsByRole. A role whose KPI producer does not exist must not
    // issue a request whose 404 the consumer would then render as measured values.
    if (!this.baseEndpoints.kpis) {
      return BaseRoleService.noProducer(this.role, 'kpis')
    }

    try {
      console.log(`📊 [BaseRoleService.getKPIs] Llamando a ${this.baseEndpoints.kpis} para rol: ${this.role}`, options.disableCache ? '(sin caché)' : '')
      const response = await apiClient.get(this.baseEndpoints.kpis, {
        params: { role: this.role, ...params },
        disableCache: options.disableCache || false
      })
      console.log(`📊 [BaseRoleService.getKPIs] Respuesta cruda:`, response)
      // Backend retorna { success: true, data: {...} }, extraer datos internos
      const responseData = response.data
      console.log(`📊 [BaseRoleService.getKPIs] responseData:`, responseData)
      const result = responseData.data || responseData
      console.log(`📊 [BaseRoleService.getKPIs] Resultado final:`, result)
      return result
    } catch (error) {
      console.warn(`⚠️ Backend no disponible para ${this.role} KPIs:`, error.message)
      
      return this.getFallbackKPIs()
    }
  }

  async executeAction(actionName, params = {}) {
    if (this.role === ROUTE_OPERATOR_ROLE || !this.baseEndpoints.actions) {
      return {
        ...ROUTE_OPERATOR_DISABLED_SURFACE,
        action: actionName,
        timestamp: new Date().toISOString()
      }
    }

    try {
      const response = await apiClient.post(this.baseEndpoints.actions, {
        action: actionName,
        role: this.role,
        params
      })
      return response.data
    } catch (error) {
      console.warn(`⚠️ No se pudo ejecutar acción ${actionName} para ${this.role}:`, error.message)
      
      return {
        success: false,
        action: actionName,
        status: 'ACTION_FAILED',
        message: `Accion ${actionName} no ejecutada: backend no confirmo la operacion`,
        timestamp: new Date().toISOString()
      }
    }
  }

  getFallbackDashboardData() {
    return {
      role: this.role,
      status: 'NOT_AVAILABLE',
      message: 'Datos no disponibles desde backend real',
      data: null,
      timestamp: new Date().toISOString()
    }
  }

  /**
   * P13-V5A-R5 — this used to return `kpis: []`, and an empty ARRAY reads as a measured
   * "no KPIs" rather than as "the read did not happen". The failure state now carries no
   * business container at all, so a consumer cannot mistake it for an observation.
   */
  getFallbackKPIs() {
    return {
      role: this.role,
      status: 'NOT_AVAILABLE',
      code: 'ROLE_KPI_READ_FAILED',
      message: 'KPIs no disponibles desde backend real'
    }
  }

  // Método utilitario para formatear datos
  formatKPIValue(value, type = 'number') {
    if (value === null || value === undefined) return 'N/A'
    
    switch (type) {
      case 'currency':
        return new Intl.NumberFormat('es-DO', {
          style: 'currency',
          currency: 'DOP'
        }).format(value)
      case 'percentage':
        return `${value.toFixed(1)}%`
      case 'number':
        return value.toLocaleString('es-DO')
      default:
        return value.toString()
    }
  }

  // P13-V7B (refutation): `validatePermission` — a client-side permission matcher with its own '*' and
  // prefix wildcards — was REMOVED. Nothing called it; it was the service-side twin of the deleted
  // `useRolePermissions` table, and a client permission list decides nothing.
}
